(() => {
  const values = [16, 24, 32, 40, 48, 50, 64, 72, 80];
  const segments = ['6,2 34,2 38,6 34,10 6,10 2,6', '36,8 40,12 40,34 36,38 32,34 32,12', '36,42 40,46 40,68 36,72 32,68 32,46', '6,70 34,70 38,74 34,78 6,78 2,74', '4,42 8,46 8,68 4,72 0,68 0,46', '4,8 8,12 8,34 4,38 0,34 0,12', '6,36 34,36 38,40 34,44 6,44 2,40'];
  const digitMap = ['abcdef', 'bc', 'abdeg', 'abcdg', 'bcfg', 'acdfg', 'acdefg', 'abc', 'abcdefg', 'abcdfg'];

  class ProductCurrentSelector extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this.control = this.querySelector('[data-current-control]');
      this.range = this.querySelector('input[type="range"]');
      this.output = this.querySelector('output');
      this.digits = this.querySelector('.current-control__digits');
      this.status = this.querySelector('.current-control__status');
      this.ticks = [...this.querySelectorAll('.current-control__ticks span')];
      if (!this.control || !this.range || !this.output || !this.digits || !this.status) return;

      this._onInput = () => this.update(Number(this.range.value));
      this.range.addEventListener('input', this._onInput);
      this._onMotionChange = event => {
        if (event.matches) this.revealAll();
      };
      this._onFocus = () => this.revealAll();
      this._motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (this._motion.addEventListener) this._motion.addEventListener('change', this._onMotionChange);
      else this._motion.addListener(this._onMotionChange);

      this.update(Number(this.range.value));
      this.setupVisibility();
      this.setupReveal();
    }

    disconnectedCallback() {
      if (!this._connected) return;
      this._connected = false;
      if (this.range && this._onInput) this.range.removeEventListener('input', this._onInput);
      if (this._motion && this._onMotionChange) {
        if (this._motion.removeEventListener) this._motion.removeEventListener('change', this._onMotionChange);
        else this._motion.removeListener(this._onMotionChange);
      }
      if (this.control && this._onFocus) this.control.removeEventListener('focusin', this._onFocus);
      if (this._visibilityObserver) this._visibilityObserver.disconnect();
      if (this._revealObserver) this._revealObserver.disconnect();
      this._visibilityObserver = null;
      this._revealObserver = null;
    }

    update(index) {
      const safeIndex = Math.max(0, Math.min(values.length - 1, Number.isFinite(index) ? index : values.length - 1));
      const value = values[safeIndex];
      const maximum = safeIndex === values.length - 1;
      this.output.value = value;
      this.output.textContent = value;
      this.range.setAttribute('aria-valuetext', `${value} amps${maximum ? ', maximum output' : ''}`);
      this.control.style.setProperty('--fraction', safeIndex / (values.length - 1));
      this.control.classList.toggle('is-max', maximum);
      this.status.textContent = maximum ? 'MAX OUTPUT' : 'SELECTED CURRENT';
      this.ticks.forEach((tick, tickIndex) => tick.classList.toggle('is-selected', tickIndex === safeIndex));
      this.draw(value);
    }

    draw(value) {
      this.digits.innerHTML = String(value).split('').map(digit => `<svg viewBox="0 0 40 80" focusable="false">${segments.map((points, index) => `<polygon points="${points}" class="${digitMap[Number(digit)].includes('abcdefg'[index]) ? 'on' : ''}"/>`).join('')}</svg>`).join('');
    }

    setupVisibility() {
      if (!('IntersectionObserver' in window)) {
        this.control.classList.add('is-visible');
        return;
      }
      this._visibilityObserver = new IntersectionObserver(entries => {
        this.control.classList.toggle('is-visible', entries.some(entry => entry.isIntersecting));
      }, { threshold: .6 });
      this._visibilityObserver.observe(this.control);
    }

    setupReveal() {
      if (this.dataset.scrollReveal !== 'true') return;
      const targets = [this.querySelector('.current__image'), this.control, ...this.querySelectorAll('.current__copy h2, .current__copy p')].filter(Boolean);
      const reveal = target => target.classList.add('is-revealed');
      if (this.dataset.designMode === 'true' || this._motion.matches || !('IntersectionObserver' in window)) {
        targets.forEach(reveal);
        return;
      }
      this._revealObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          reveal(entry.target);
          this._revealObserver.unobserve(entry.target);
        });
      }, { threshold: .18, rootMargin: '0px 0px -8% 0px' });
      targets.forEach(target => {
        const rect = target.getBoundingClientRect();
        if (rect.top < window.innerHeight * .92 && rect.bottom > 0) reveal(target);
        else this._revealObserver.observe(target);
      });
      this.control.addEventListener('focusin', this._onFocus, { once: true });
    }

    revealAll() {
      this.querySelectorAll('.current__image, .current-control, .current__copy h2, .current__copy p').forEach(element => element.classList.add('is-revealed'));
      if (this._revealObserver) this._revealObserver.disconnect();
    }
  }

  if (!customElements.get('product-current-selector')) customElements.define('product-current-selector', ProductCurrentSelector);
})();
